import styled from "@emotion/styled";
import React from "react";
import { observer } from "mobx-react-lite";
import Button from "@components/Button";
import Text from "@components/Text";
import { Row } from "@components/Flex";
import SizedBox from "@components/SizedBox";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import { useFormatArea, useLang } from "@src/hooks/useLang";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import { ReactComponent as TrashIcon } from "@assets/icons/trash.svg";

/**
 * Authoring panel for yield correction zones.
 *
 * Rendered only while the correction tool is active — outside it the map just
 * shows corrected values with no zone outlines. Sits above the yield stats
 * card so both stay readable.
 */

export const Root = styled.div`
  position: absolute;
  left: 24px;
  bottom: 16px;
  width: 320px;
  max-height: calc(100% - 120px);
  overflow-y: auto;
  background: #ffffff;
  border-radius: 6px;
  padding: 16px;
  box-sizing: border-box;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.1);
  z-index: 12;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    left: 12px;
    right: 12px;
    width: auto;
  }
`;

export const Title = styled(Text)`
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: #494c5b;
`;

export const Hint = styled(Text)`
  font-size: 12px;
  line-height: 18px;
  color: #899093;
`;

export const ToolButton = styled.button<{ active?: boolean }>`
  flex: 1;
  height: 32px;
  border: 1px solid ${({ active }) => (active ? "#68ad18" : "#e6e8eb")};
  background: ${({ active }) => (active ? "rgba(104, 173, 24, 0.1)" : "#fff")};
  color: #494c5b;
  border-radius: 4px;
  font-family: "Nunito Sans";
  font-size: 13px;
  font-weight: ${({ active }) => (active ? 700 : 400)};
  cursor: pointer;
`;

const PercentInput = styled.input`
  width: 72px;
  height: 32px;
  padding: 0 8px;
  box-sizing: border-box;
  border: 1px solid #e6e8eb;
  border-radius: 4px;
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #494c5b;
  outline: none;
  &:focus {
    border-color: #68ad18;
  }
`;

const NameInput = styled(PercentInput)`
  width: 100%;
`;

const ZoneRow = styled(Row)`
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-top: 1px solid #f0f1f3;
`;

const ZoneLabel = styled(Text)`
  font-size: 13px;
  color: #494c5b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  path {
    fill: #899093;
  }
  &:hover path {
    fill: #e5484d;
  }
`;

/**
 * A saved zone's percentage, shown as the change it makes: a positive
 * reduction reads "-30%", a negative one raises the yield and reads "+30%".
 */
const signedPercent = (reductionPercent: number | null): string => {
  const pct = reductionPercent ?? 0;
  return pct < 0 ? `+${-pct}%` : `-${pct}%`;
};

const YieldAdjustPanel: React.FC = () => {
  const vm = useFieldsScreenVM();
  const lang = useLang();
  const formatArea = useFormatArea();
  const t = lang.fields;

  if (!vm.isYieldAdjustEdit) return null;

  const hasDraft = vm.adjustDraftCount > 0;

  return (
    <Root>
      <Title>{t.yieldAdjustTitle}</Title>
      <SizedBox height={4} />
      <Hint>{t.yieldAdjustHint}</Hint>
      <SizedBox height={12} />

      <Row style={{ gap: 8 }}>
        <ToolButton
          active={vm.adjustTool === "hex"}
          onClick={vm.stopAdjustLasso}
          type="button"
        >
          {t.yieldAdjustToolHex}
        </ToolButton>
        <ToolButton
          active={vm.adjustTool === "lasso"}
          onClick={vm.startAdjustLasso}
          type="button"
        >
          {t.yieldAdjustToolLasso}
        </ToolButton>
      </Row>

      <SizedBox height={12} />
      <Row style={{ gap: 8 }}>
        <ToolButton
          active={vm.adjustMode === "percent"}
          onClick={() => vm.setAdjustMode("percent")}
          type="button"
        >
          {t.yieldAdjustModePercent}
        </ToolButton>
        <ToolButton
          active={vm.adjustMode === "field_average"}
          onClick={() => vm.setAdjustMode("field_average")}
          type="button"
        >
          {t.yieldAdjustModeAverage}
        </ToolButton>
      </Row>

      {vm.adjustMode === "percent" && (
        <>
          <SizedBox height={10} />
          <Row alignItems="center" style={{ gap: 8 }}>
            <Hint>{t.yieldAdjustChangeBy}</Hint>
            <PercentInput
              type="number"
              min={-100}
              max={100}
              value={String(vm.adjustPercent)}
              onChange={(e) => vm.setAdjustPercent(Number(e.target.value))}
            />
            <Hint>%</Hint>
          </Row>
        </>
      )}

      <SizedBox height={10} />
      <NameInput
        type="text"
        placeholder={t.yieldAdjustNamePlaceholder}
        value={vm.adjustName}
        onChange={(e) => vm.setAdjustName(e.target.value)}
      />

      <SizedBox height={12} />
      <Row style={{ gap: 8 }}>
        <Button
          kind="primary"
          disabled={!hasDraft || vm.adjustSaving}
          onClick={vm.saveYieldAdjustment}
        >
          {vm.adjustSaving
            ? t.yieldAdjustSaving
            : `${t.yieldAdjustSave} (${vm.adjustDraftCount})`}
        </Button>
        <Button
          kind="secondary"
          disabled={!hasDraft || vm.adjustSaving}
          onClick={vm.clearAdjustDraft}
        >
          {t.yieldAdjustClear}
        </Button>
      </Row>

      {vm.yieldAdjustments.length > 0 && (
        <>
          <SizedBox height={16} />
          <Hint>{t.yieldAdjustSavedZones}</Hint>
          <SizedBox height={4} />
          {vm.yieldAdjustments.map((adj) => (
            <ZoneRow key={adj.id}>
              <ZoneLabel style={{ flex: 1 }}>
                {adj.name || t.yieldAdjustUnnamed}
                {" · "}
                {adj.mode === "percent"
                  ? signedPercent(adj.reductionPercent)
                  : t.yieldAdjustModeAverage}
                {" · "}
                {formatArea(adj.areaHectares)}
              </ZoneLabel>
              <IconButton
                type="button"
                disabled={vm.adjustSaving}
                onClick={() => vm.deleteYieldAdjustment(adj.id)}
                title={t.yieldAdjustDelete}
              >
                <TrashIcon />
              </IconButton>
            </ZoneRow>
          ))}
        </>
      )}

      <SizedBox height={12} />
      <Button kind="outline" onClick={vm.exitYieldAdjustEdit}>
        {t.yieldAdjustDone}
      </Button>
    </Root>
  );
};

export default observer(YieldAdjustPanel);
